/**
 * Public CSAT Survey Page — /s/:token
 *
 * Renders standalone (no CRM navbar / sidebar / auth). Client just opens the
 * link, answers 40 questions across 4 sections, and hits submit.
 *
 * Brand-neutral by default. Shows the IB's own logo + name (never "Underads
 * CRM"). Once a custom survey domain is configured, only this page is served
 * on that host — client will never see the CRM URL.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Star, CheckCircle2, Loader2, Send, ChevronRight, ChevronLeft, MessageSquareQuote, Users, GraduationCap, Package, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_ORDER = ['community', 'education', 'product', 'signal'];
const CATEGORY_META = {
  community: { Icon: Users,          gradient: 'from-rose-500 to-orange-500',    tint: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-900',    accent: '#F43F5E' },
  education: { Icon: GraduationCap,  gradient: 'from-blue-500 to-indigo-500',    tint: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-900',    accent: '#3B82F6' },
  product:   { Icon: Package,        gradient: 'from-emerald-500 to-teal-500',   tint: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', accent: '#10B981' },
  signal:    { Icon: Radio,          gradient: 'from-amber-500 to-yellow-500',   tint: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   accent: '#F59E0B' },
};

// UI text translations — used by public survey based on channel language.
const I18N = {
  ms: {
    loading: 'Memuat naik survey...',
    expired: 'Link survey ini telah tamat tempoh.',
    already_completed: 'Survey ini sudah dijawab sebelum ini. Terima kasih!',
    not_found_title: 'Survey tidak tersedia',
    not_found_body: 'Sila hubungi admin untuk link baru.',
    completed_title: 'Terima kasih!',
    completed_body: 'Anda telah menjawab survey ini.',
    subtitle: 'Customer Satisfaction Survey',
    progress: 'Progress',
    submit_step: 'Submit',
    part_of: (a, b) => `Bahagian ${a} dari ${b}`,
    section_intro: 'Sila jawab dengan ikhlas — jawapan anda akan digunakan untuk improve service kami. Setiap bahagian bernilai 25% dari total score.',
    comment_optional: 'Komen (optional)...',
    open_prompt: 'Cadangan / Feedback anda',
    open_placeholder: 'Kongsi cadangan atau kritikan anda di sini...',
    contact_title: 'Hampir siap!',
    contact_body: 'Tinggalkan kontak (optional) jika anda mahu kami follow-up. Kalau tidak, terus tekan submit.',
    name: 'Nama', name_ph: 'Nama anda',
    email: 'Email', email_ph: 'email@example.com',
    phone: 'Telefon', phone_ph: '+60 12 345 6789',
    summary_title: 'Ringkasan jawapan',
    back: 'Kembali', next: 'Seterusnya',
    submit: 'Hantar Survey', submitting: 'Menghantar...',
    thanks_title: 'Terima Kasih!', thanks_body: 'Feedback anda amat bermakna untuk kami.',
    your_score: 'Score anda', total_csat: 'CSAT Score keseluruhan',
    footer_note: 'Feedback ini akan digunakan untuk penambahbaikan.',
    close_page: 'You may close this page.',
    confidential: 'Confidential · Feedback anda dilindungi',
  },
  en: {
    loading: 'Loading survey...',
    expired: 'This survey link has expired.',
    already_completed: 'You have already answered this survey. Thank you!',
    not_found_title: 'Survey unavailable',
    not_found_body: 'Please contact the admin for a new link.',
    completed_title: 'Thank you!',
    completed_body: 'You have already answered this survey.',
    subtitle: 'Customer Satisfaction Survey',
    progress: 'Progress',
    submit_step: 'Submit',
    part_of: (a, b) => `Part ${a} of ${b}`,
    section_intro: 'Please answer honestly — your feedback will be used to improve our service. Each section is worth 25% of the total score.',
    comment_optional: 'Comment (optional)...',
    open_prompt: 'Your suggestion / feedback',
    open_placeholder: 'Share your suggestion or criticism here...',
    contact_title: 'Almost done!',
    contact_body: 'Leave your contact (optional) if you want us to follow up. Otherwise, just hit submit.',
    name: 'Name', name_ph: 'Your name',
    email: 'Email', email_ph: 'email@example.com',
    phone: 'Phone', phone_ph: '+60 12 345 6789',
    summary_title: 'Answer summary',
    back: 'Back', next: 'Next',
    submit: 'Submit Survey', submitting: 'Submitting...',
    thanks_title: 'Thank You!', thanks_body: 'Your feedback means a lot to us.',
    your_score: 'Your score', total_csat: 'Overall CSAT score',
    footer_note: 'This feedback will be used for improvement.',
    close_page: 'You may close this page.',
    confidential: 'Confidential · Your feedback is protected',
  },
  id: {
    loading: 'Memuat survei...',
    expired: 'Tautan survei ini sudah kedaluwarsa.',
    already_completed: 'Anda sudah menjawab survei ini sebelumnya. Terima kasih!',
    not_found_title: 'Survei tidak tersedia',
    not_found_body: 'Silakan hubungi admin untuk mendapatkan tautan baru.',
    completed_title: 'Terima kasih!',
    completed_body: 'Anda sudah menjawab survei ini.',
    subtitle: 'Survei Kepuasan Pelanggan',
    progress: 'Progres',
    submit_step: 'Kirim',
    part_of: (a, b) => `Bagian ${a} dari ${b}`,
    section_intro: 'Silakan jawab dengan jujur — masukan Anda akan digunakan untuk meningkatkan layanan kami. Setiap bagian bernilai 25% dari total skor.',
    comment_optional: 'Komentar (opsional)...',
    open_prompt: 'Saran / Masukan Anda',
    open_placeholder: 'Bagikan saran atau kritik Anda di sini...',
    contact_title: 'Hampir selesai!',
    contact_body: 'Silakan tinggalkan kontak (opsional) jika ingin kami tindak lanjuti. Jika tidak, langsung tekan kirim.',
    name: 'Nama', name_ph: 'Nama Anda',
    email: 'Email', email_ph: 'email@example.com',
    phone: 'Telepon', phone_ph: '+62 812 3456 7890',
    summary_title: 'Ringkasan jawaban',
    back: 'Kembali', next: 'Berikutnya',
    submit: 'Kirim Survei', submitting: 'Mengirim...',
    thanks_title: 'Terima Kasih!', thanks_body: 'Masukan Anda sangat berarti bagi kami.',
    your_score: 'Skor Anda', total_csat: 'Skor CSAT keseluruhan',
    footer_note: 'Masukan ini akan digunakan untuk peningkatan layanan.',
    close_page: 'Anda dapat menutup halaman ini.',
    confidential: 'Rahasia · Masukan Anda terlindungi',
  },
};

function StarRow({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(n => {
        const active = (hover || value || 0) >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            className="transition-transform hover:scale-110 focus:outline-none"
            data-testid={`star-${n}`}
            aria-label={`${n} star`}
          >
            <Star
              size={30}
              className={`transition-colors ${active ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.4)]' : 'text-gray-300'}`}
            />
          </button>
        );
      })}
      <span className="ml-2 text-xs text-gray-500 tabular-nums w-6">
        {value ? `${value}/5` : ''}
      </span>
    </div>
  );
}

export default function PublicSurveyPage({ mode = 'token' }) {
  // 'mode' = 'token' → uses /s/:token endpoint
  //         'slug'  → uses /csat/public/channel/:slug endpoint (short URL)
  const params = useParams();
  const identifier = mode === 'slug' ? params.slug : params.token;
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [resultScores, setResultScores] = useState(null);

  // UI text is ALWAYS English (buttons / headers / placeholders / prompts).
  // Only the questions themselves come translated from the backend based on
  // the channel's configured language (BM / EN / ID). This gives the survey
  // a consistent, professional feel regardless of audience language.
  const t = I18N.en;

  const endpoint = mode === 'slug'
    ? `${API}/csat/public/channel/${identifier}`
    : `${API}/csat/public/survey/${identifier}`;

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(endpoint);
        setSurvey(r.data);
        // Error messages also use English (UI language)
        if (r.data.expired) setError(I18N.en.expired);
        if (r.data.already_completed) setError(I18N.en.already_completed);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Survey not found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [endpoint]);

  const setAnswer = (qid, cat, patch) => {
    setAnswers(prev => ({
      ...prev,
      [qid]: { rating: null, comment: '', ...prev[qid], category: cat, ...patch },
    }));
  };

  const currentCat = CATEGORY_ORDER[step];
  const currentQuestions = useMemo(() => {
    if (!survey?.questions || !currentCat) return [];
    return survey.questions[currentCat] || [];
  }, [survey, currentCat]);

  const totalAnswered = Object.values(answers).filter(a => a.rating || (a.comment && a.comment.trim())).length;
  const totalQuestions = useMemo(() => {
    if (!survey?.questions) return 0;
    return Object.values(survey.questions).reduce((sum, arr) => sum + arr.length, 0);
  }, [survey]);
  const progressPct = totalQuestions ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  // Can we advance from this step? Require at least one rating in this category
  const canAdvance = useMemo(() => {
    if (step >= 4) return true;
    const cat = CATEGORY_ORDER[step];
    return currentQuestions.some(q => {
      const a = answers[q.id];
      if (!a) return false;
      if (q.question_type === 'open_text') return !!(a.comment && a.comment.trim());
      return !!a.rating;
    });
  }, [step, currentQuestions, answers]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        client_name: contact.name || undefined,
        client_email: contact.email || undefined,
        client_phone: contact.phone || undefined,
        answers: Object.entries(answers).map(([qid, a]) => ({
          question_id: qid,
          category: a.category,
          rating: a.rating || null,
          comment: (a.comment || '').trim() || null,
        })),
      };
      const r = await axios.post(endpoint, payload);
      setResultScores(r.data);
      setStep(5);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Loading -----------------------------------------------------------
  if (loading) {
    return (
      <div className="light min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{t.loading}</p>
        </div>
      </div>
    );
  }

  // --- Error / expired / already-completed -------------------------------
  if (error || survey?.expired || survey?.already_completed) {
    const isCompleted = survey?.already_completed;
    return (
      <div className="light min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 md:p-10 text-center">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${isCompleted ? 'bg-emerald-100' : 'bg-slate-100'}`}>
            <CheckCircle2 className={`w-8 h-8 ${isCompleted ? 'text-emerald-600' : 'text-slate-400'}`} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {isCompleted ? t.completed_title : t.not_found_title}
          </h1>
          <p className="text-slate-600 text-sm">
            {error || (isCompleted ? t.completed_body : t.not_found_body)}
          </p>
        </div>
      </div>
    );
  }

  // --- Done screen -------------------------------------------------------
  if (step === 5 && resultScores) {
    return (
      <div className="light min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-8 text-center text-white">
            {survey?.brand_logo ? (
              <img src={survey.brand_logo} alt={survey?.ib_name || ''} className="w-16 h-16 mx-auto rounded-2xl object-cover ring-4 ring-white/30 shadow-lg mb-3" />
            ) : (
              <div className="w-20 h-20 mx-auto rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-3">
                <CheckCircle2 className="w-12 h-12" strokeWidth={2.5} />
              </div>
            )}
            <h1 className="text-3xl font-bold mb-1">{t.thanks_title}</h1>
            <p className="text-emerald-50 text-sm">{t.thanks_body}</p>
            {survey?.ib_name && (
              <p className="text-emerald-100/80 text-xs mt-2">— {survey.ib_name}</p>
            )}
          </div>
          <div className="p-6 md:p-8">
            <p className="text-center text-slate-500 text-xs uppercase tracking-wider mb-4">{t.your_score}</p>
            <div className="text-center mb-6">
              <p className="text-6xl font-bold text-slate-900 tabular-nums">{resultScores.total_score}<span className="text-3xl text-slate-400">%</span></p>
              <p className="text-xs text-slate-500 mt-1">{t.total_csat}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {CATEGORY_ORDER.map(c => {
                const s = resultScores.scores?.[c];
                const meta = CATEGORY_META[c];
                const Icon = meta.Icon;
                return (
                  <div key={c} className={`p-3 rounded-xl ${meta.tint} ${meta.border} border`}>
                    <div className="flex items-center gap-1.5">
                      <Icon size={12} className={meta.text} strokeWidth={2.5} />
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">{survey.labels[c]}</p>
                    </div>
                    <p className={`text-xl font-bold mt-1 tabular-nums ${meta.text}`}>{s !== null && s !== undefined ? `${s}%` : '—'}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-xs text-slate-400 mt-6">{t.footer_note}<br />{t.close_page}</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Main survey UI ----------------------------------------------------
  const meta = CATEGORY_META[currentCat];
  const CurrentIcon = meta?.Icon;
  const isContactStep = step === 4;

  return (
    <div className="light min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header with brand */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {survey?.brand_logo ? (
              <img src={survey.brand_logo} alt={survey?.ib_name || ''} className="w-11 h-11 rounded-xl object-cover ring-2 ring-slate-100 shadow-sm" />
            ) : (
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-800 to-slate-600 flex items-center justify-center text-white font-bold text-base shadow-sm">
                {(survey?.ib_name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-semibold text-slate-900 text-sm md:text-base leading-tight">{survey?.ib_name}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">{t.subtitle}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{t.progress}</p>
            <p className="text-sm font-bold text-slate-900 tabular-nums">{progressPct}%</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className={`h-full bg-gradient-to-r ${meta ? meta.gradient : 'from-emerald-500 to-teal-500'} transition-all duration-500`} style={{ width: `${progressPct}%` }} />
        </div>
        {/* Step dots */}
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-3 flex items-center justify-center gap-2">
          {CATEGORY_ORDER.map((c, i) => {
            const StepIcon = CATEGORY_META[c].Icon;
            return (
              <button
                key={c}
                onClick={() => setStep(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  i === step ? `bg-gradient-to-r ${CATEGORY_META[c].gradient} text-white shadow-md`
                  : i < step ? 'bg-slate-200 text-slate-600' : 'text-slate-400 hover:text-slate-600'
                }`}
                data-testid={`step-${c}`}
              >
                <StepIcon size={13} strokeWidth={2.25} />
                <span className="hidden sm:inline">{survey?.labels?.[c]?.split(' ')[0]}</span>
              </button>
            );
          })}
          <button
            onClick={() => setStep(4)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              isContactStep ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <Send size={12} strokeWidth={2.25} />
            <span className="hidden sm:inline">{t.submit_step}</span>
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {!isContactStep && step === 0 && survey?.brand_logo && (
          /* Hero banner on step 0 — makes brand feel prominent */
          <div className="mb-6 text-center">
            <div className="inline-block relative">
              <img
                src={survey.brand_logo}
                alt={survey?.ib_name || ''}
                className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white shadow-xl mx-auto"
              />
            </div>
            <p className="mt-3 text-slate-500 text-xs uppercase tracking-widest">— {survey?.ib_name} —</p>
          </div>
        )}
        {!isContactStep && (
          <>
            {/* Category header */}
            <div className="mb-6 text-center">
              <div className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold ${meta.tint} ${meta.text} mb-3`}>
                {CurrentIcon && <CurrentIcon size={13} strokeWidth={2.5} />}
                {t.part_of(step + 1, 4)}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                {survey?.labels?.[currentCat]}
              </h1>
            </div>

            {/* Questions */}
            <div className="space-y-4">
              {currentQuestions.map((q, idx) => {
                const a = answers[q.id] || {};
                const isOpen = q.question_type === 'open_text';
                return (
                  <div
                    key={q.id}
                    className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`question-${q.id}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`w-7 h-7 rounded-full ${meta.tint} ${meta.text} flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                        {idx + 1}
                      </div>
                      <p className="text-sm md:text-base text-slate-800 leading-relaxed flex-1">
                        {q.question_text}
                      </p>
                    </div>
                    {!isOpen && (
                      <div className="ml-10">
                        <StarRow
                          value={a.rating}
                          onChange={(n) => setAnswer(q.id, currentCat, { rating: n })}
                        />
                        {q.allow_comment && (
                          <textarea
                            value={a.comment || ''}
                            onChange={(e) => setAnswer(q.id, currentCat, { comment: e.target.value })}
                            placeholder={t.comment_optional}
                            className="mt-3 w-full text-sm rounded-lg border-2 border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 resize-none shadow-sm"
                            style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                            rows={2}
                            data-testid={`comment-${q.id}`}
                          />
                        )}
                      </div>
                    )}
                    {isOpen && (
                      <div className="ml-10">
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                          <MessageSquareQuote size={14} />
                          <span>{t.open_prompt}</span>
                        </div>
                        <textarea
                          value={a.comment || ''}
                          onChange={(e) => setAnswer(q.id, currentCat, { comment: e.target.value, rating: null })}
                          placeholder={t.open_placeholder}
                          className="w-full text-sm rounded-lg border-2 border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 resize-none shadow-sm"
                          style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                          rows={3}
                          data-testid={`open-comment-${q.id}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {isContactStep && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t.contact_title}</h1>
            <p className="text-sm text-slate-500 mb-6">{t.contact_body}</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">{t.name}</label>
                <input
                  type="text"
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  placeholder={t.name_ph}
                  className="w-full text-sm rounded-lg border-2 border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 shadow-sm"
                  style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                  data-testid="contact-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">{t.email}</label>
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder={t.email_ph}
                    className="w-full text-sm rounded-lg border-2 border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 shadow-sm"
                    style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">{t.phone}</label>
                  <input
                    type="tel"
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    placeholder={t.phone_ph}
                    className="w-full text-sm rounded-lg border-2 border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 shadow-sm"
                    style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
                    data-testid="contact-phone"
                  />
                </div>
              </div>
            </div>
            {/* Summary */}
            <div className="mt-8 p-4 bg-slate-50 rounded-xl">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t.summary_title}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {CATEGORY_ORDER.map(c => {
                  const answered = (survey?.questions?.[c] || []).filter(q => {
                    const a = answers[q.id];
                    return a && (a.rating || (a.comment && a.comment.trim()));
                  }).length;
                  const total = (survey?.questions?.[c] || []).length;
                  const SumIcon = CATEGORY_META[c].Icon;
                  return (
                    <div key={c} className="flex items-center justify-between px-2 py-1.5">
                      <span className="flex items-center gap-1.5"><SumIcon size={12} className="text-slate-600" strokeWidth={2.25} /> {survey?.labels?.[c]}</span>
                      <span className="font-mono font-semibold text-slate-700">{answered}/{total}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Navigation footer */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="prev-btn"
          >
            <ChevronLeft size={16} /> {t.back}
          </button>
          {!isContactStep ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-r ${meta.gradient}`}
              data-testid="next-btn"
            >
              {t.next} <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              data-testid="submit-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {submitting ? t.submitting : t.submit}
            </button>
          )}
        </div>
      </main>

      <footer className="max-w-3xl mx-auto px-4 md:px-8 pb-8 text-center">
        <p className="text-[10px] text-slate-400">{t.confidential}</p>
      </footer>
    </div>
  );
}
